import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getInsights,
  dismissInsight,
  resolveInsight,
  triggerAnalysis,
  type Insight,
} from '../api/client';
import InsightCard from '../components/insights/InsightCard';
import InsightDetail from '../components/insights/InsightDetail';
import {
  Sparkles,
  AlertTriangle,
  AlertCircle,
  Lightbulb,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';

const typeFilters = [
  { label: 'All Types', value: 'all' },
  { label: 'Page Performance', value: 'page_performance' },
  { label: 'Traffic Source', value: 'traffic_source' },
  { label: 'UX Issue', value: 'ux_issue' },
  { label: 'Form Optimization', value: 'form_optimization' },
  { label: 'Conversion Flow', value: 'conversion_flow' },
  { label: 'Error Impact', value: 'error_impact' },
  { label: 'Device & Browser', value: 'device_browser' },
  { label: 'Session Analysis', value: 'session_deep' },
  { label: 'Pattern Detection', value: 'pattern_detection' },
];

const categoryFilters = [
  { label: 'All Categories', value: 'all' },
  { label: 'Critical', value: 'critical' },
  { label: 'Warning', value: 'warning' },
  { label: 'Opportunity', value: 'opportunity' },
  { label: 'Positive', value: 'positive' },
];

const statusFilters = [
  { label: 'Active', value: 'active' },
  { label: 'Dismissed', value: 'dismissed' },
  { label: 'Resolved', value: 'resolved' },
];

export default function InsightsPage() {
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [page, setPage] = useState(1);
  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const [analysisStatus, setAnalysisStatus] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['insights', typeFilter, categoryFilter, statusFilter, page],
    queryFn: () =>
      getInsights({
        type: typeFilter === 'all' ? undefined : typeFilter,
        category: categoryFilter === 'all' ? undefined : categoryFilter,
        status: statusFilter,
        page,
        limit: 20,
      }),
  });

  const insights = data?.insights || [];
  const totalPages = data?.pagination?.totalPages || 1;

  // Count by category from current results for summary cards
  const criticalCount = insights.filter((i) => i.category === 'critical').length;
  const warningCount = insights.filter((i) => i.category === 'warning').length;
  const opportunityCount = insights.filter((i) => i.category === 'opportunity').length;
  const positiveCount = insights.filter((i) => i.category === 'positive').length;

  const dismissMutation = useMutation({
    mutationFn: dismissInsight,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insights'] }),
  });

  const resolveMutation = useMutation({
    mutationFn: resolveInsight,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insights'] }),
  });

  const analysisMutation = useMutation({
    mutationFn: () => triggerAnalysis(),
    onSuccess: () => {
      setAnalysisStatus('Analysis started successfully!');
      queryClient.invalidateQueries({ queryKey: ['insights'] });
      setTimeout(() => setAnalysisStatus(null), 3000);
    },
    onError: () => {
      setAnalysisStatus('Failed to start analysis.');
      setTimeout(() => setAnalysisStatus(null), 3000);
    },
  });

  function handleFilterChange(setter: (v: string) => void, value: string) {
    setter(value);
    setPage(1);
  }

  const summaryCards = [
    { label: 'Critical', count: criticalCount, color: 'bg-red-50 border-red-200 text-red-700', icon: AlertTriangle, iconColor: 'text-red-500' },
    { label: 'Warning', count: warningCount, color: 'bg-yellow-50 border-yellow-200 text-yellow-700', icon: AlertCircle, iconColor: 'text-yellow-500' },
    { label: 'Opportunity', count: opportunityCount, color: 'bg-blue-50 border-blue-200 text-blue-700', icon: Lightbulb, iconColor: 'text-blue-500' },
    { label: 'Positive', count: positiveCount, color: 'bg-green-50 border-green-200 text-green-700', icon: CheckCircle2, iconColor: 'text-green-500' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">AI Insights</h1>
        <div className="flex items-center gap-3">
          {analysisStatus && (
            <span className="text-sm text-gray-600">{analysisStatus}</span>
          )}
          <button
            onClick={() => analysisMutation.mutate()}
            disabled={analysisMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4" />
            {analysisMutation.isPending ? 'Running...' : 'Run Analysis'}
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className={clsx('rounded-lg border p-4', card.color)}>
              <div className="flex items-center gap-2">
                <Icon className={clsx('h-4 w-4', card.iconColor)} />
                <span className="text-sm font-medium">{card.label}</span>
              </div>
              <p className="mt-1 text-2xl font-bold">{card.count}</p>
            </div>
          );
        })}
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={typeFilter}
          onChange={(e) => handleFilterChange(setTypeFilter, e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          {typeFilters.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => handleFilterChange(setCategoryFilter, e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          {categoryFilters.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          {statusFilters.map((f) => (
            <button
              key={f.value}
              onClick={() => handleFilterChange(setStatusFilter, f.value)}
              className={clsx(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                statusFilter === f.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Insights List */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
        </div>
      ) : insights.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white py-16">
          <Sparkles className="mb-4 h-12 w-12 text-gray-300" />
          <h3 className="mb-1 text-lg font-semibold text-gray-900">No insights yet</h3>
          <p className="mb-4 text-sm text-gray-500">Run your first analysis to get AI-powered insights.</p>
          <button
            onClick={() => analysisMutation.mutate()}
            disabled={analysisMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4" />
            Run Analysis
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {insights.map((insight) => (
            <InsightCard
              key={insight.id}
              insight={insight}
              onDismiss={(id) => dismissMutation.mutate(id)}
              onResolve={(id) => resolveMutation.mutate(id)}
              onViewDetail={setSelectedInsight}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
            className="btn-secondary p-2 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
            className="btn-secondary p-2 disabled:opacity-50"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Detail Modal */}
      <InsightDetail
        insight={selectedInsight}
        onClose={() => setSelectedInsight(null)}
        onDismiss={(id) => dismissMutation.mutate(id)}
        onResolve={(id) => resolveMutation.mutate(id)}
      />
    </div>
  );
}
